import { useCallback, useEffect } from 'react';

/**
 * Selector for navigable menu items
 */
const MENU_ITEM_SELECTOR = '[role="menuitem"], [data-menu-item]';

export interface UseKeyboardNavigationOptions {
  /**
   * Whether keyboard navigation is active
   */
  isActive: boolean;
  /**
   * Ref to the container element containing navigable items
   */
  containerRef: React.RefObject<HTMLElement | null>;
  /**
   * Callback when an item is selected (Enter/Space pressed)
   */
  onSelect?: (element: HTMLElement) => void;
  /**
   * Callback when escape is pressed (optional, handled separately in most cases)
   */
  onEscape?: () => void;
  /**
   * Custom selector for navigable items
   * @default '[role="menuitem"], [data-menu-item]'
   */
  itemSelector?: string;
  /**
   * Whether to wrap around when reaching the end/start
   * @default true
   */
  wrap?: boolean;
}

export interface UseKeyboardNavigationReturn {
  /**
   * Get all navigable items within the container
   */
  getNavigableItems: () => HTMLElement[];
  /**
   * Focus the next item in the list
   */
  focusNext: () => void;
  /**
   * Focus the previous item in the list
   */
  focusPrevious: () => void;
  /**
   * Focus the first item in the list
   */
  focusFirst: () => void;
  /**
   * Focus the last item in the list
   */
  focusLast: () => void;
}

/**
 * Hook for keyboard navigation within dropdown menus.
 *
 * Features:
 * - Arrow Up/Down to navigate between items
 * - Enter/Space to select/activate focused item
 * - Home/End to jump to first/last item
 * - Works with any container of focusable menu items
 *
 * @example
 * ```tsx
 * const { getNavigableItems } = useKeyboardNavigation({
 *   isActive: isOpen,
 *   containerRef: menuRef,
 *   onSelect: (el) => el.click(),
 * });
 *
 * return <div ref={menuRef} role="menu">...</div>;
 * ```
 */
export function useKeyboardNavigation({
  isActive,
  containerRef,
  onSelect,
  onEscape,
  itemSelector = MENU_ITEM_SELECTOR,
  wrap = true,
}: UseKeyboardNavigationOptions): UseKeyboardNavigationReturn {
  /**
   * Get all navigable items within the container
   */
  const getNavigableItems = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];
    const elements =
      containerRef.current.querySelectorAll<HTMLElement>(itemSelector);
    return Array.from(elements).filter((el) => {
      // Check if element is disabled
      if (
        el.hasAttribute('disabled') ||
        el.getAttribute('aria-disabled') === 'true'
      ) {
        return false;
      }
      // Check visibility
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      return true;
    });
  }, [containerRef, itemSelector]);

  /**
   * Get the index of the currently focused item
   */
  const getCurrentIndex = useCallback((): number => {
    const items = getNavigableItems();
    const activeElement = document.activeElement as HTMLElement;
    return items.indexOf(activeElement);
  }, [getNavigableItems]);

  /**
   * Focus the first item in the list
   */
  const focusFirst = useCallback(() => {
    const items = getNavigableItems();
    items.at(0)?.focus();
  }, [getNavigableItems]);

  /**
   * Focus the last item in the list
   */
  const focusLast = useCallback(() => {
    const items = getNavigableItems();
    items.at(-1)?.focus();
  }, [getNavigableItems]);

  /**
   * Focus the next item in the list
   */
  const focusNext = useCallback(() => {
    const items = getNavigableItems();
    if (items.length === 0) return;

    const currentIndex = getCurrentIndex();
    let nextIndex: number;

    if (currentIndex === -1) {
      // Nothing focused, go to first
      nextIndex = 0;
    } else if (currentIndex >= items.length - 1) {
      // At last item
      nextIndex = wrap ? 0 : items.length - 1;
    } else {
      nextIndex = currentIndex + 1;
    }

    items.at(nextIndex)?.focus();
  }, [getNavigableItems, getCurrentIndex, wrap]);

  /**
   * Focus the previous item in the list
   */
  const focusPrevious = useCallback(() => {
    const items = getNavigableItems();
    if (items.length === 0) return;

    const currentIndex = getCurrentIndex();
    let prevIndex: number;

    if (currentIndex === -1) {
      // Nothing focused, go to last
      prevIndex = items.length - 1;
    } else if (currentIndex <= 0) {
      // At first item
      prevIndex = wrap ? items.length - 1 : 0;
    } else {
      prevIndex = currentIndex - 1;
    }

    items.at(prevIndex)?.focus();
  }, [getNavigableItems, getCurrentIndex, wrap]);

  // Handle keyboard events
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle if focus is within or should be within the container
      if (!containerRef.current) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          focusNext();
          break;

        case 'ArrowUp':
          event.preventDefault();
          focusPrevious();
          break;

        case 'Home':
          event.preventDefault();
          focusFirst();
          break;

        case 'End':
          event.preventDefault();
          focusLast();
          break;

        case 'Enter':
        case ' ':
          // Only handle if the active element is a navigable item
          if (containerRef.current.contains(document.activeElement)) {
            const items = getNavigableItems();
            const activeElement = document.activeElement as HTMLElement;
            if (items.includes(activeElement)) {
              // Space should be prevented to avoid scrolling
              if (event.key === ' ') {
                event.preventDefault();
              }
              // Call onSelect callback if provided
              if (onSelect) {
                onSelect(activeElement);
              }
              // Note: Don't call .click() here - let the native behavior work for Enter
              // or let the onSelect callback handle it
            }
          }
          break;

        case 'Escape':
          if (onEscape) {
            event.preventDefault();
            onEscape();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isActive,
    containerRef,
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,
    getNavigableItems,
    onSelect,
    onEscape,
  ]);

  return {
    getNavigableItems,
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,
  };
}

export default useKeyboardNavigation;
